import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { map, filter } from 'rxjs/operators';
import { ErrorMessages } from '../../models/error-messages.model';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-error-messages',
    templateUrl: './error-messages.component.html',
    styleUrls: ['./error-messages.component.scss'],
    standalone: true,
})
export class ErrorMessagesComponent implements OnInit, ControlValueAccessor {
    private readonly destroyRef = inject(DestroyRef);
    private readonly ngControl = inject<NgControl>(NgControl);

    /**
     * ErrorMessages to consider when rendering the error message
     * depending on {@link NgControl.errors}.
     *
     * If a {@link string} is provided it will always be taken as error message.
     *
     * If a {@link ErrorMessages} object is given the order of the attributes will
     * determine which error message is shown.
     * The first attribute name matching a key in {@link NgControl.errors}
     * will be taken.
     * If no matching key is found the {@link ErrorMessages.default } message will be shown.
     *
     * null value indicates no error message will be shown.
     * (The form will still mark the input as erroneous though.)
     */
    public messages = input<ErrorMessages | string>('Eingabe inkorrekt');
    private messages$ = toObservable(this.messages);

    protected errorId = input<string>();

    protected displayMessage = signal<string | null>(null);

    public constructor() {
        this.ngControl.valueAccessor = this;
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */
    // we don't actually need those, but they are
    // mandatory when implementing ControlValueAccessor
    public writeValue(_obj: never): void {}
    public registerOnChange(_fn: never): void {}
    public registerOnTouched(_fn: never): void {}
    /* eslint-enable */

    public ngOnInit(): void {
        this.subscribeToErrorChanges();
    }

    private subscribeToErrorChanges(): void {
        const errorsPipe = this.ngControl.statusChanges.pipe(
            map(() => this.ngControl.errors),
            startWith(this.ngControl.errors),
        );

        combineLatest([errorsPipe, this.messages$])
            .pipe(
                filter(([, messages]) => !!messages),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([errors, messages]) => {
                if (errors === null || messages === null) {
                    this.displayMessage.set(null);
                } else {
                    if (typeof messages === 'string') {
                        this.displayMessage.set(messages);
                    } else {
                        const errorKeys: string[] = Object.keys(errors);
                        const messageKeys: string[] = Object.keys(messages);

                        const key = errorKeys.find((errorKey) =>
                            messageKeys.some((messageKey) => errorKey === messageKey),
                        );

                        const msg = messages[key] ?? messages.default ?? null;
                        if (typeof msg === 'function') {
                            this.displayMessage.set(msg());
                        } else if (msg === null || typeof msg === 'string') {
                            this.displayMessage.set(msg);
                        }
                    }
                }
            });
    }
}
